/**
 * Rejection reason mapping — mirrors the list exposed by the admin email service.
 * Keep this in sync with the admin-side rejection reasons list.
 *
 * Each entry provides:
 *  - name: short human-readable title shown to the resident
 *  - description: longer explanation (matches admin-side wording)
 *  - actionStep: which petDetails section the user should be routed to in order
 *    to resolve the issue (one of 'petInfo' | 'vetInfo' | 'documents' | 'registrationDetails')
 *  - actionLabel: the CTA button label shown in the banner
 */

export type RejectionActionStep = 'petInfo' | 'vetInfo' | 'documents' | 'registrationDetails';

export interface RejectionReasonInfo {
  code: string;
  name: string;
  description: string;
  actionStep: RejectionActionStep;
  actionLabel: string;
}

// Canonical list — codes match the admin email service at
// /api/email/rejection-reasons (source of truth).
const REASON_DEFS: RejectionReasonInfo[] = [
  {
    code: 'incomplete_documentation',
    name: 'Additional Documents Needed',
    description: 'Some required documents are missing from the registration.',
    actionStep: 'documents',
    actionLabel: 'Upload missing documents',
  },
  {
    code: 'expired_vaccination',
    name: 'Updated Vaccination Records Needed',
    description: "Pet's vaccination records on file have expired.",
    actionStep: 'documents',
    actionLabel: 'Upload updated vaccination records',
  },
  {
    code: 'invalid_esa_letter',
    name: 'Updated ESA Documentation Needed',
    description: 'ESA documentation does not meet necessary requirements.',
    actionStep: 'documents',
    actionLabel: 'Update ESA documentation',
  },
  {
    code: 'breed_restrictions',
    name: 'Additional Review Required',
    description: 'Pet breed is restricted under community policy.',
    actionStep: 'petInfo',
    actionLabel: 'Review pet information',
  },
  {
    code: 'missing_pet_photo',
    name: 'Pet Photo Needed',
    description: 'Pet registration is missing a clear photo.',
    actionStep: 'petInfo',
    actionLabel: 'Add pet photo',
  },
  {
    code: 'unclear_documents',
    name: 'Clearer Documents Needed',
    description: 'Submitted documents are not legible.',
    actionStep: 'documents',
    actionLabel: 'Re-upload clearer documents',
  },
  {
    code: 'weight_restrictions',
    name: 'Additional Review Required',
    description: "Pet's weight exceeds the maximum allowed under community policy.",
    actionStep: 'petInfo',
    actionLabel: 'Review pet information',
  },
  {
    code: 'insurance_required',
    name: 'Pet Insurance Verification Needed',
    description: 'Pet registration requires proof of active pet insurance.',
    actionStep: 'documents',
    actionLabel: 'Upload insurance proof',
  },
];

/**
 * Known aliases — defensive mapping from plausible variant codes to the
 * canonical code in REASON_DEFS above. Not strictly needed today, but cheap
 * insurance against future drift in the admin email service.
 */
const REASON_ALIASES: Record<string, string> = {
  // weight
  weight_limit_exceeded: 'weight_restrictions',
  weight_limit: 'weight_restrictions',
  weight_exceeded: 'weight_restrictions',
  // breed
  restricted_breed: 'breed_restrictions',
  breed_restricted: 'breed_restrictions',
  // documents
  missing_documents: 'incomplete_documentation',
  additional_documents_needed: 'incomplete_documentation',
  illegible_documents: 'unclear_documents',
  clearer_documents_needed: 'unclear_documents',
  documents_not_legible: 'unclear_documents',
  // vaccination
  vaccination_expired: 'expired_vaccination',
  updated_vaccination_needed: 'expired_vaccination',
  // esa
  invalid_esa_documentation: 'invalid_esa_letter',
  esa_documentation_invalid: 'invalid_esa_letter',
  updated_esa_documentation_needed: 'invalid_esa_letter',
  // photo
  pet_photo_needed: 'missing_pet_photo',
  photo_needed: 'missing_pet_photo',
  // insurance
  missing_insurance: 'insurance_required',
  insurance_missing: 'insurance_required',
  insurance_verification_needed: 'insurance_required',
  pet_insurance_verification_needed: 'insurance_required',
};

export const REJECTION_REASONS: Record<string, RejectionReasonInfo> = REASON_DEFS.reduce(
  (acc, r) => {
    acc[r.code] = r;
    return acc;
  },
  {} as Record<string, RejectionReasonInfo>
);

/**
 * Humanize a snake_case / kebab-case identifier into a title-cased string
 * for use in fallback display (e.g. "weight_restrictions" → "Weight Restrictions").
 */
const humanizeCode = (code: string): string => {
  return code
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
};

/**
 * Accept either a code (preferred) or an arbitrary reason string and return
 * a display-friendly representation. If we don't recognize the code, we fall
 * back to showing the raw string so nothing is lost.
 */
export const resolveRejectionReason = (raw: string | undefined | null): RejectionReasonInfo | null => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Direct code match
  if (REJECTION_REASONS[trimmed]) return REJECTION_REASONS[trimmed];

  // Normalized snake_case lookup
  const normalized = trimmed.toLowerCase().replace(/[\s-]+/g, '_');
  if (REJECTION_REASONS[normalized]) return REJECTION_REASONS[normalized];

  // Alias lookup (handles known variant codes from the admin email service)
  const aliased = REASON_ALIASES[normalized];
  if (aliased && REJECTION_REASONS[aliased]) return REJECTION_REASONS[aliased];

  // Fallback — unknown code. Humanize it for the name and surface the raw
  // code as the description so the resident still has actionable information,
  // and route them to the documents step by default.
  const looksLikeCode = /^[a-z0-9_\-]+$/i.test(trimmed);
  return {
    code: trimmed,
    name: looksLikeCode ? humanizeCode(trimmed) : 'Registration Rejected',
    description: looksLikeCode
      ? 'Please review your registration and address the issue noted by the reviewer.'
      : trimmed,
    actionStep: 'documents',
    actionLabel: 'Review registration',
  };
};

/**
 * A timeline entry from status_history can contain:
 *   - reason: a single code, human text, or comma-separated codes
 *   - note:   free-text note written by admin
 *
 * This helper returns the list of resolved reasons plus the admin note.
 */
export const parseRejectionEntry = (entry: { reason?: string | null; note?: string | null } | null | undefined): {
  reasons: RejectionReasonInfo[];
  note: string;
} => {
  if (!entry) return { reasons: [], note: '' };

  const note = (entry.note || '').trim();
  const reasonRaw = (entry.reason || '').trim();
  if (!reasonRaw) return { reasons: [], note };

  const parts = reasonRaw.split(',').map((p) => p.trim()).filter(Boolean);
  const reasons = parts
    .map((p) => resolveRejectionReason(p))
    .filter((r): r is RejectionReasonInfo => !!r);

  return { reasons, note };
};

/**
 * Resolve a list of raw rejection reason codes (e.g. pet.rejection_reasons
 * from the API) into display-ready entries. Unknown codes are preserved via
 * the fallback path in `resolveRejectionReason`.
 */
export const resolveRejectionReasons = (raws: Array<string | null | undefined> | null | undefined): RejectionReasonInfo[] => {
  if (!raws || !Array.isArray(raws)) return [];
  return raws
    .map((r) => resolveRejectionReason(r))
    .filter((r): r is RejectionReasonInfo => !!r);
};
